import React, { useEffect } from 'react';
import { Check, Heart, Mail, ArrowRight, Home, BookOpen } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const SubscribeThankYouPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, name, subscriptionId } = location.state || {};

  useEffect(() => {
    // Fire Google Ads conversion tracking
    if (typeof gtag !== 'undefined') {
      gtag('event', 'conversion', {
        'send_to': 'AW-17595569822/subscription_conversion',
        'transaction_id': subscriptionId || '',
        'value': 1,
        'currency': 'USD'
      });
      console.log('Google Ads conversion tracked for subscription');
    }

    // Fire additional conversion events for better tracking
    if (typeof gtag !== 'undefined') {
      gtag('event', 'sign_up', {
        'method': 'email',
        'value': 1
      });
    }
  }, [subscriptionId]);

  const nextSteps = [
    {
      icon: Mail,
      title: 'Check Your Email',
      description: 'We\'ve sent a welcome email with your first spiritual insight. Check your inbox and add us to your contacts.',
      action: 'Open Email App',
      actionHref: 'mailto:'
    },
    {
      icon: BookOpen,
      title: 'Explore Sermons',
      description: 'Start your spiritual journey with our biblical teachings and sermon collection from El Bethel Global.',
      action: 'Watch Sermons',
      actionHref: '/sermons'
    },
    {
      icon: Heart,
      title: 'Submit Prayer Requests',
      description: 'Our prayer team is ready to intercede for your needs. Share your prayer requests with confidence.',
      action: 'Request Prayer',
      actionHref: '/prayer'
    }
  ];

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0 min-h-screen bg-gradient-to-br from-green-50 to-white">
      <section className="py-12 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Success Message */}
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12 mb-12">
              <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-8">
                <Check className="w-12 h-12 text-white" />
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 font-display">
                Welcome to the Family!
              </h1>
              
              {name ? (
                <p className="text-xl lg:text-2xl text-gray-600 mb-6 font-sans">
                  Thank you, <span className="font-bold text-[#f17b21]">{name}</span>! 
                  Your subscription to El Bethel Ministries has been confirmed.
                </p>
              ) : (
                <p className="text-xl lg:text-2xl text-gray-600 mb-6 font-sans">
                  Your subscription to El Bethel Ministries has been confirmed!
                </p>
              )}

              {email && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
                  <p className="text-green-800 font-semibold font-sans">
                    Confirmation sent to: {email}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-center space-x-2 text-[#f17b21] mb-8">
                <Heart className="w-6 h-6" />
                <span className="text-lg font-bold font-sans">You're now part of our ministry community!</span>
                <Heart className="w-6 h-6" />
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4 font-display">What's Next?</h3>
                <p className="text-gray-700 font-sans leading-relaxed">
                  You'll receive your first email with spiritual insights soon. 
                  Our weekly content is designed to encourage your faith and provide practical 
                  biblical guidance for Christian living.
                </p>
              </div>
            </div>

            {/* Next Steps */}
            <div className="mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-8 font-display">
                Your Next Steps
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {nextSteps.map((step, index) => {
                  const IconComponent = step.icon;
                  return (
                    <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                      <div className="w-16 h-16 bg-[#f17b21] rounded-full flex items-center justify-center mx-auto mb-6">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-4 font-display">{step.title}</h3>
                      <p className="text-gray-600 mb-6 leading-relaxed font-sans">{step.description}</p>
                      <button
                        onClick={() => {
                          if (step.actionHref.startsWith('/')) {
                            navigate(step.actionHref);
                          } else {
                            window.open(step.actionHref);
                          }
                        }}
                        className="w-full bg-[#f17b21] hover:bg-[#d86919] text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
                      >
                        {step.action}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Continue Browsing */}
            <div className="bg-gradient-to-r from-[#f17b21] to-[#ff9649] rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl lg:text-3xl font-bold mb-4 font-display">Continue Exploring</h3>
              <p className="text-lg text-white/90 mb-6 font-sans">
                While you wait for your first email, explore our ministry content and connect with our community.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center space-x-2 bg-white text-[#f17b21] hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
                >
                  <Home className="w-5 h-5" />
                  <span>Return Home</span>
                </button>
                <button
                  onClick={() => navigate('/sermons')}
                  className="inline-flex items-center space-x-2 border-2 border-white text-white hover:bg-white hover:text-[#f17b21] px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Watch Sermons</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SubscribeThankYouPage;